import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CompanySelect } from 'src/components/CompanySelect/CompanySelect'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { getRole } from 'src/service/authService'

interface CreateDirectoryModalProps {
  open: boolean
  onClose: () => void
  onCreate: (name: string, description?: string, companyId?: string) => Promise<any>
}

export function CreateDirectoryModal({
  open,
  onClose,
  onCreate,
}: CreateDirectoryModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isTecnoit = getRole() === 'TECNOIT'
  const { isGlobalAdminSelected, selectedCompanyData } = useGlobalSelectedCompany()

  // Track selected company separately for reliable submit
  const [selectedCompany, setSelectedCompany] = useState<any>(null)

  // useForm only needed to provide `control` for CompanySelect
  const { control, setValue } = useForm({
    defaultValues: { companyId: undefined as any },
  })

  // Pre-fill from sidebar selection when modal opens
  useEffect(() => {
    if (isTecnoit && open) {
      if (!isGlobalAdminSelected() && selectedCompanyData) {
        setValue('companyId', selectedCompanyData)
        setSelectedCompany(selectedCompanyData)
      } else {
        setValue('companyId', undefined)
        setSelectedCompany(null)
      }
    }
  }, [open, selectedCompanyData])

  const handleCompanyChange = (company: any) => {
    setSelectedCompany(company)
  }

  const handleSubmit = async () => {
    if (!name.trim()) return
    const companyId = isTecnoit && selectedCompany
      ? (selectedCompany.id || selectedCompany.companyId)
      : undefined
    setIsSubmitting(true)
    const res = await onCreate(name.trim(), description.trim() || undefined, companyId)
    setIsSubmitting(false)
    if (res) {
      setName('')
      setDescription('')
      setValue('companyId', undefined)
      setSelectedCompany(null)
      onClose()
    }
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setValue('companyId', undefined)
    setSelectedCompany(null)
    onClose()
  }

  const isSubmitDisabled = !name.trim() || isSubmitting || (isTecnoit && !selectedCompany)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fileRepository.modal.createDirectory.title')}</DialogTitle>
      <DialogContent>
        {isTecnoit && (
          <CompanySelect
            sx={{ mt: 1, gridColumn: 'span 12' }}
            control={control}
            onChangeAction={handleCompanyChange}
          />
        )}
        <TextField
          autoFocus
          fullWidth
          label={t('fileRepository.modal.createDirectory.nameLabel')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          label={t('fileRepository.modal.createDirectory.descriptionLabel')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('general.button.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('fileRepository.modal.createDirectory.button')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
